import { array, number, object } from "yup";
// @ts-expect-error pure js
import { stringMax } from "./utils";

export const RepartitionKeyUnit = {
  PERCENT: "percent",
  SHARE: "share",
} as const;

export type RepartitionKeyUnit =
  (typeof RepartitionKeyUnit)[keyof typeof RepartitionKeyUnit];

export interface RepartitionKeyEntry {
  domaine?: string;
  id_bien?: number;
  id_property_folder?: number;
  montant: number;
  affectation?: string;
}

export interface RepartitionKey {
  id: number;
  nom: string;
  unit: RepartitionKeyUnit;
  /** Dénominateur des tantièmes ; null = somme des tantièmes des lots. */
  total_shares?: number | null;
  repartition: RepartitionKeyEntry[];
}

/** Somme des tantièmes affectés aux lots de la clé. */
export function repartitionKeyAllocatedShares(key: RepartitionKey): number {
  return key.repartition.reduce((acc, r) => acc + (Number(r.montant) || 0), 0);
}

/**
 * Total des tantièmes d'une clé : celui saisi, sinon la somme des lots. Il peut
 * dépasser la somme quand des biens sont sortis de la clé.
 */
export function repartitionKeyTotalShares(key: RepartitionKey): number {
  return Number(key.total_shares) > 0
    ? Number(key.total_shares)
    : repartitionKeyAllocatedShares(key);
}

/**
 * Diviseur qui ramène un `montant` de la clé à une fraction du total :
 * 100 pour des pourcentages, le total des tantièmes sinon.
 */
export function repartitionKeyBase(key: RepartitionKey): number {
  return key.unit === RepartitionKeyUnit.SHARE
    ? repartitionKeyTotalShares(key)
    : 100;
}

export const repartitionSchema = object({
  domaine: stringMax(250).required(" "),
  id_bien: number().optional(),
  id_property_folder: number().optional(),
  montant: number().min(0, " ").required(" "),
  affectation: stringMax(250).required(" "),
});

export const repartitionKeySchema = object({
  nom: stringMax(250).required(" "),
  unit: stringMax(50)
    .oneOf(Object.values(RepartitionKeyUnit), " ")
    .required(" "),
  total_shares: number().min(0, " ").nullable(),
  repartition: array().of(repartitionSchema),
});

export const financeConfigSchema = object({
  keyword: stringMax(250).required(" "),
  id_tenant: number().integer(),
  id_bien: number().integer(),
  id_property_folder: number().integer(),
  type: stringMax(250).required(" "),
  affectation: stringMax(250).required(" "),
  montant: number().optional(),
  repartition: array().of(repartitionSchema),
});

export const repartitionKeyInitialValues = {
  nom: "",
  unit: RepartitionKeyUnit.PERCENT,
  total_shares: "",
  repartition: [],
};

export const financeConfigInitialValues = {
  keyword: "",
  id_tenant: "",
  id_bien: "",
  id_property_folder: "",
  type: "",
  domaine: "",
  affectation: "",
  montant: "",
  repartition: [],
};
